import { router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { BrandImageUploader } from "@/components/brand-image-uploader"
import { RankBlazon } from "@/components/rank/rank-blazon"
import { brandSettingsProgressionRankImagePath } from "@/routes"

const IMAGE_ACCEPT = "image/png,image/webp"
const IMAGE_MAX_BYTES = 2 * 1024 * 1024
const IMAGE_MIN_DIMENSION = 128

/**
 * OKS-539 — Per-rank blazon uploader for the rank-ladder editor.
 *
 * Lives inside each rank `<fieldset>` in `<RankLadderForm>`. Because that
 * fieldset is nested in the ladder's single atomic PATCH `<Form>`, this
 * uploader CANNOT nest its own `<Form>` (invalid HTML). Instead it submits
 * imperatively via Inertia's `router` to the DEDICATED per-rank endpoint
 * (`brandSettingsProgressionRankImagePath`), fully decoupled from the
 * ladder save (PRD Q4) — a bad ladder edit never blocks a blazon change
 * and vice-versa. Still pure Inertia (no shadcn Form / RHF / zod); reuses
 * the shared `<BrandImageUploader>` widget verbatim.
 *
 * - Pick a valid file → optimistic preview + `router.patch` (multipart)
 *   to attach/replace. Success → server redirect refreshes props (flash
 *   toast "Blazon updated"); every render surface picks up the new URL.
 * - "Remove image" → `router.delete` (purge → revert to tinted default).
 * - Client pre-check mirrors the server rules for a fast inline hint;
 *   server-side validation stays authoritative (surfaced via `onError`,
 *   scoped to THIS row so a sibling row's error never bleeds in).
 */
export interface RankBlazonUploaderProps {
  rankId: number
  rankName: string
  /** 1-based ladder position — drives the no-image default tint. */
  tier: number
  /** Brand ladder size — drives the no-image default tint. */
  tierCount: number
  /** Current saved blazon URL, or null (⇒ tinted default). */
  imageUrl: string | null
  /** Hides all affordances at the read-only tier. */
  readOnly?: boolean
}

export function RankBlazonUploader({
  rankId,
  rankName,
  tier,
  tierCount,
  imageUrl,
  readOnly = false,
}: RankBlazonUploaderProps) {
  const { t } = useTranslation()
  const [serverError, setServerError] = useState<string | null>(null)

  // Client pre-check — fast inline hint mirroring the server rules
  // (PNG/WebP, ≤2 MB, square ≥128 px). The server stays authoritative.
  const validate = (file: File): Promise<string | null> => {
    if (!IMAGE_ACCEPT.split(",").includes(file.type)) {
      return Promise.resolve(t("badges.form.image.errors.type"))
    }
    if (file.size > IMAGE_MAX_BYTES) {
      return Promise.resolve(t("badges.form.image.errors.size"))
    }
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file)
      const probe = new window.Image()
      probe.onload = () => {
        URL.revokeObjectURL(url)
        const square = probe.naturalWidth === probe.naturalHeight
        const big = probe.naturalWidth >= IMAGE_MIN_DIMENSION
        resolve(square && big ? null : t("badges.form.image.errors.dimensions"))
      }
      probe.onerror = () => {
        URL.revokeObjectURL(url)
        resolve(t("badges.form.image.errors.dimensions"))
      }
      probe.src = url
    })
  }

  const onFilePicked = (file: File) => {
    setServerError(null)
    router.patch(
      brandSettingsProgressionRankImagePath(rankId),
      { brand_rank: { image: file } },
      {
        forceFormData: true,
        preserveScroll: true,
        // Scope the server error to THIS row (shared props would bleed a
        // sibling row's `errors.image` onto every uploader).
        onError: (errors) => {
          const message = errors.image
          setServerError(
            Array.isArray(message) ? (message[0] ?? null) : (message ?? null),
          )
        },
      },
    )
  }

  const onRemove = () => {
    setServerError(null)
    router.delete(brandSettingsProgressionRankImagePath(rankId), {
      preserveScroll: true,
    })
  }

  return (
    <BrandImageUploader
      currentUrl={imageUrl}
      name="brand_rank[image]"
      label={t("settings.brand.progression.ranks.blazon.label")}
      helper={t("settings.brand.progression.ranks.blazon.helper")}
      removeLabel={t("settings.brand.progression.ranks.blazon.remove")}
      accept={IMAGE_ACCEPT}
      validate={validate}
      onFilePicked={onFilePicked}
      onRemove={onRemove}
      error={serverError ? [serverError] : undefined}
      readOnly={readOnly}
      testId={`rank-blazon-uploader-${rankId}`}
      renderPreview={(url) => (
        <RankBlazon
          input={{ tier, tierCount, name: rankName, imageUrl: url }}
          size="crest"
          decorative
        />
      )}
    />
  )
}

export default RankBlazonUploader
